import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { FieldMapping, ImportOption, OptionsMap } from '../typings'
import { selectAllBranches } from '@user/store/branches/slice'
import {
  selectAllSalesFunnels,
  selectAllSalesFunnelStages,
} from '@user/store/salesFunnel'
import { selectAllStatesProvinces } from '@user/store/dictionaries'
import { selectAllSalesTeams } from '@user/store/salesTeam/slice'
import { selectAllSources } from '@user/store/sources/slice'
import { selectAllCampaigns } from '@user/store/campaigns/slice'
import { selectAllTargetPests } from '@user/store/targetPests'
import { selectAllLocationTypes } from '@user/store/locationTypes'
import { selectAllDivisions } from '@user/store/divisions'
import { selectAllUsers } from '@user/store/users'
import { LEAD_STATUSES } from '@user/components/Leads/LeadCard/typings'

import { options as bestContactTimes } from '@user/components/Formik/BestContactTimeSelect/hooks/useData'
import { options as bestContactMethods } from '@user/components/Formik/BestContactMethodSelect/hooks/useData'
import { selectAllServiceProductsForTenant } from '@user/store/serviceProducts/slice'
import { selectAllServiceFrequencies } from '@user/store/serviceFrequency'
import { useSelectCustomFieldData } from '@user/store/customFields'

export function useGetOptions() {
  const allSalesFunnels = useSelector(selectAllSalesFunnels)
  const allBranches = useSelector(selectAllBranches)
  const allStatesProvinces = useSelector(selectAllStatesProvinces)
  const allSalesTeams = useSelector(selectAllSalesTeams)
  const allLeadSources = useSelector(selectAllSources)
  const allCampaigns = useSelector(selectAllCampaigns)
  const allTargetPests = useSelector(selectAllTargetPests)
  const allLocationTypes = useSelector(selectAllLocationTypes)
  const allDivisions = useSelector(selectAllDivisions)
  const allUsers = useSelector(selectAllUsers)
  const allStages = useSelector(selectAllSalesFunnelStages)
  const allServices = useSelector(selectAllServiceProductsForTenant)
  const serviceFrequencies = useSelector(selectAllServiceFrequencies)
  const allContactMethods = bestContactMethods
  const allContactTimes = bestContactTimes
  const customFieldData = useSelectCustomFieldData()

  const getOptions = useCallback((options: ImportOption[]) => {
    return options.map((s) => {
      const value = s.description ? `${s.name} - ${s.description}` : s.name
      const label = s.isDefault ? `(Default) ${value}` : value
      return { label: label, value: s.value ? s.value : value }
    })
  }, [])

  const allUsersOptions = useMemo(() => {
    const options = allUsers
      .filter((x) => x.isActive)
      .map((x) => ({
        name: x.fullName ? `${x.fullName} - ${x.emailAddress}` : '',
        value: x.id,
      }))
    options.unshift({ name: 'Unassigned', value: '' })

    return options.sort((a, b) => a.name.localeCompare(b.name))
  }, [allUsers])

  const allServiceOptions = useMemo(() => {
    const allTenantServices = Array.from(allServices.values()).flat()

    const uniqueServices = Array.from(
      new Map(
        allTenantServices.map((service) => [service.name, service]),
      ).values(),
    )

    // Sort the result by 'name'
    return uniqueServices.sort((a, b) => a.name.localeCompare(b.name))
  }, [allServices])

  const fieldMappings: FieldMapping[] = useMemo(
    () => [
      { key: 'salesTeams', options: allSalesTeams || [] },
      { key: 'salesFunnels', options: allSalesFunnels || [] },
      { key: 'branches', options: allBranches || [] },
      { key: 'leadSources', options: allLeadSources || [] },
      { key: 'campaigns', options: allCampaigns || [] },
      // Special case
      {
        key: 'targetPests',
        options: allTargetPests.map((t) => ({ name: t.name })) || [],
      },
      { key: 'locationTypes', options: allLocationTypes || [] },
      { key: 'divisions', options: allDivisions || [] },
      { key: 'users', options: allUsersOptions || [] },
      {
        key: 'salesFunnelLeadStages',
        options:
          Array.from(
            new Map(
              allStages
                .filter(
                  (x) =>
                    x.sectionType === 'Lead' &&
                    x.name.toLowerCase() !== LEAD_STATUSES.Disqualified,
                )
                .map((x) => [x.name, x]),
            ).values(),
          ) || [],
      },
      {
        key: 'salesFunnelOpportunityStages',
        options:
          Array.from(
            new Map(
              allStages
                .filter((x) => x.sectionType === 'Opportunity' && !x.closed)
                .map((x) => [x.name, x]),
            ).values(),
          ) || [],
      },
      {
        key: 'services',
        options: allServiceOptions || [],
      },
      {
        key: 'contactMethods',
        options:
          allContactMethods.map((t) => ({
            name: t.label ?? '',
            id: t.value,
          })) || [],
      },
      {
        key: 'contactTimes',
        options:
          allContactTimes.map((t) => ({ name: t.label ?? '', id: t.value })) ||
          [],
      },
      {
        key: 'frequencies',
        options:
          serviceFrequencies.map((t) => ({
            name: `${t.annualOccurrences} - ${t.description}`,
            id: t.id,
          })) || [],
      },
    ],
    [
      allSalesTeams,
      allSalesFunnels,
      allBranches,
      allLeadSources,
      allCampaigns,
      allTargetPests,
      allLocationTypes,
      allDivisions,
      allUsersOptions,
      allStages,
      allServiceOptions,
      allContactMethods,
      allContactTimes,
      serviceFrequencies,
    ],
  )

  const options: OptionsMap = useMemo(() => {
    const result: Partial<OptionsMap> = {}
    fieldMappings.forEach(({ key, options }) => {
      result[key] = getOptions(options)
    })
    return result as OptionsMap
  }, [fieldMappings, getOptions])

  const statesProvinces = useMemo(() => {
    if (allStatesProvinces) {
      return allStatesProvinces
        .sort((a, b) =>
          a.abbreviation.localeCompare(b.abbreviation, undefined, {
            sensitivity: 'base',
          }),
        )
        .map((s) => ({ label: s.abbreviation, value: s.abbreviation }))
    }
    return []
  }, [allStatesProvinces])

  return {
    ...options,
    statesProvinces,
    customFieldData,
  }
}
